import * as type from '../mutation-type/orders'
import axios from 'axios'
import Vue from 'vue'
import { ORDER_STATUS } from '~/constants'
import ordersService from '../services/orders'
import Swal from 'sweetalert2'

let fetchOrdersSeq = 0
let draftListSeq = 0

/**
 * Surface a MOQ (422) validation error as a popup. Returns true if handled.
 */
const handleMoqError = (error) => {
  const moq = error?.response?.status === 422 && error?.response?.data?.errors?.moq
  if (!moq || !moq.length) {
    return false
  }
  Swal.fire({
    icon: 'warning',
    title: 'Minimum Order Quantity',
    html: moq.join('<br>'),
    confirmButtonText: 'OK'
  })
  return true
}

// state
export const state = {
  orders: [],
  completedOrders: [],
  draftOrders: [],
  pageOrders: [],
  totalOrderPagePages: 1,
  totalOrderPages: 1,
  totalOrders: 1,
  totalPageOrders: 1,
  currentPage: 1,
  currentOrderPage: 1,
  totalCompletedOrderPages: 1,
  totalCompletedOrders: 1,
  currentCompletedOrderPage: 1,
  orderSummaryPreview: {
    total: 0,
    items: []
  },
  newOrderMode: 'emptyOrder',
  newOrderProduct: null,
  activeDraftOrder: null,
  // Lightweight, draft-only feed for the header Orders dropdown. Kept separate from
  // `orders` so the dropdown never pulls (or clobbers) the heavy active-order cart graph.
  draftOrdersList: [],
  draftListPage: 1,
  draftListTotalPages: 1,
  unitSummaryReportData: null,
  orderDetail: '',
  bulkDeleteDraftOrderMessage: '',
  orderHasProductQuantity: false,
  exceedQuantityProducts: [],
  insufficientQuantitySelectedProduct: null,
  actionRequiredSelectedOption: null,
  validationResponse: {
    status: false,
    message: ''
  }
}

// getters
export const getters = {
  draftOrders: (state) => state.orders?.filter(item => item.is_draft === ORDER_STATUS.DRAFT) ?? [],
  processingOrders: (state) => state.orders?.filter(item => item.status === ORDER_STATUS.PROCESSING) ?? [],
  completedAndBackOrders: (state) => state.completedOrders,
  pageOrders: (state) => state.pageOrders,
  draftOrderHasProducts: (state, getters) => {
    const returnArr = []

    const draftOrders = getters.draftOrders

    if (!draftOrders.length) return []

    for (const draftOrder of draftOrders) {
      for (const orderHasProduct of draftOrder.order_has_products) {
        returnArr.push(orderHasProduct)
      }
    }

    return returnArr
  },
  activeDraftOrder: (state) => state.activeDraftOrder,
  draftOrdersList: (state) => state.draftOrdersList,
  draftListPage: (state) => state.draftListPage,
  draftListTotalPages: (state) => state.draftListTotalPages,
  newOrderMode: state => state.newOrderMode,
  newOrderProduct: state => state.newOrderProduct,
  orderIsEmpty: state => state.activeDraftOrder?.order_has_products ? state.activeDraftOrder?.order_has_products?.length === 0 : true,
  unitSummaryReportData: state => state.unitSummaryReportData,
  orderDetail: state => state.orderDetail,
  bulkDeleteDraftOrderMessage: state => state.bulkDeleteDraftOrderMessage,
  orderHasProductQuantity: state => state.orderHasProductQuantity,
  exceedQuantityProducts: state => state.exceedQuantityProducts,
  insufficientQuantitySelectedProduct: state => state.insufficientQuantitySelectedProduct,
  totalOrderPages: state => state.totalOrderPages,
  totalOrders: state => state.totalOrders,
  currentPage: state => state.currentPage,
  totalCompletedOrderPages: state => state.totalCompletedOrderPages,
  totalCompletedOrders: state => state.totalCompletedOrders,
  currentCompletedOrderPage: state => state.currentCompletedOrderPage,
  currentOrderPage: state => state.currentOrderPage,
  validationResponse: state => state.validationResponse

}

// mutation
export const mutations = {

  [type.SET_ORDERS_ORDERS] (state, { orders }) {
    const activeDraftOrderId = localStorage.getItem('activeDraftOrderId')
    state.orders = orders
    state.activeDraftOrder = activeDraftOrderId ? state.orders.find(order => order.id === parseInt(activeDraftOrderId)) : false
  },
  [type.SET_ORDERS_PAGE_ORDERS] (state, { orders }) {
    state.pageOrders = orders
  },
  [type.SET_ORDERS_ORDER_SUMMARY_PREVIEW] (state, { orderSummaryPreview }) {
    state.orderSummaryPreview = orderSummaryPreview
  },
  [type.FETCH_UNIT_SUMMARY_REPORT_DATA] (state, { data }) {
    state.unitSummaryReportData = data
  },
  // The footer bar reads only `total`, so a scan-burst refresh patches just that and skips the
  // full report. The department/brand breakdowns behind the Unit Summary modal are refreshed by
  // the reload that follows the burst.
  [type.PATCH_UNIT_SUMMARY_TOTALS] (state, totals) {
    if (!totals) return

    if (!state.unitSummaryReportData) {
      state.unitSummaryReportData = { departmentItems: [], brandCategoryItems: [], total: totals }
      return
    }

    Vue.set(state.unitSummaryReportData, 'total', { ...state.unitSummaryReportData.total, ...totals })
  },
  [type.ADD_ORDER] (state, order) {
    state.orders.unshift(order)
  },
  [type.REMOVE_ORDER] (state, { orderId }) {
    state.orders = state.orders.filter(order => order.id !== orderId)
    state.draftOrdersList = state.draftOrdersList.filter(order => order.id !== orderId)
  },
  [type.ADD_ORDER_PRODUCT] (state, { orderId, productId }) {

  },
  [type.REMOVE_ORDER_PRODUCT] (state, { orderId, productId }) {

  },
  [type.ADD_ORDER_PRODUCTS] (state, { orderId, productIds }) {
  },
  [type.UPDATE_ORDER] (state, { orderId, updatedData }) {
    let updatedOrder = {}
    state.orders = state.orders.map(o => {
      if (o.id === orderId) {
        updatedOrder = {
          ...o,
          ...updatedData
        }
        return updatedOrder
      }
      return o
    })

    const activeDraftOrderId = localStorage.getItem('activeDraftOrderId')
    if (Number(orderId) === Number(activeDraftOrderId)) {
      state.activeDraftOrder = updatedOrder
    }
  },
  [type.SET_NEW_ORDER_MODE] (state, orderId) {
    state.activeDraftOrder = orderId ? state.orders.find(order => order.id === parseInt(orderId)) : null
    localStorage.setItem('activeDraftOrderId', orderId)
    state.newOrderMode = orderId
  },
  [type.SET_NEW_ORDER_PRODUCT] (state, product) {
    state.newOrderProduct = product
  },
  [type.CLEAR_NEW_ORDER_PRODUCT] (state) {
    state.newOrderProduct = null
  },
  [type.SET_ACTIVE_DRAFT_ORDER] (state, orderId) {
    state.activeDraftOrder = orderId ? state.orders.find(order => order.id === parseInt(orderId)) : null
    localStorage.setItem('activeDraftOrderId', orderId)
  },
  [type.CLEAR_ACTIVE_DRAFT_ORDER] (state) {
    state.activeDraftOrder = null
    localStorage.removeItem('activeDraftOrderId')
  },
  SET_DRAFT_ORDERS_LIST (state, list) {
    state.draftOrdersList = list
  },
  SET_DRAFT_LIST_PAGE (state, page) {
    state.draftListPage = page
  },
  SET_DRAFT_LIST_TOTAL_PAGES (state, totalPages) {
    state.draftListTotalPages = totalPages
  },
  [type.GET_ORDER_DETAIL] (state, payload) {
    state.orderDetail = payload
  },
  [type.BULK_DELETE_DRAFT_ORDER] (state, payload) {
    state.bulkDeleteDraftOrderMessage = payload
  },
  [type.ORDER_HAS_PRODUCT_QUANTITIES] (state, payload) {
    state.orderHasProductQuantity = payload
  },
  [type.SET_EXCEED_QUANTITY_PRODUCT] (state, payload) {
    state.exceedQuantityProducts = payload
  },
  [type.INSUFFICIENT_QUANTITY_SELECTED_PRODUCT] (state, payload) {
    state.insufficientQuantitySelectedProduct = payload
  },
  [type.ACTION_REQUIRED_SELECTED_OPTION] (state, payload) {
    state.actionRequiredSelectedOption = payload
  },
  [type.SET_TOTAL_ORDER_PAGES] (state, payload) {
    state.totalOrderPages = payload
  },
  [type.SET_TOTAL_PAGE_ORDER_PAGES] (state, payload) {
    state.totalOrderPagePages = payload
  },
  [type.SET_TOTAL_ORDERS] (state, payload) {
    state.totalOrders = payload
  },
  [type.SET_TOTAL_PAGE_ORDERS] (state, payload) {
    state.totalPageOrders = payload
  },
  [type.SET_CURRENT_PAGE] (state, payload) {
    state.currentPage = payload
  },
  [type.SET_CURRENT_PAGE_ORDER] (state, payload) {
    state.currentOrderPage = payload
  },
  [type.SET_CURRENT_COMPLETED_ORDER_PAGE] (state, page) {
    state.currentCompletedOrderPage = page
  },
  [type.SET_TOTAL_COMPLETED_ORDER_PAGES] (state, totalPages) {
    state.totalCompletedOrderPages = totalPages
  },
  [type.SET_TOTAL_COMPLETED_ORDERS] (state, totalOrders) {
    state.totalCompletedOrders = totalOrders
  },
  [type.SET_COMPLETED_ORDERS] (state, { orders }) {
    state.completedOrders = orders
  },
  [type.SET_VALIDATION_RESPONSE] (state, payload) {
    state.validationResponse = payload
  }
}

// actions
export const actions = {
  updateActiveDraftOrder ({ commit, rootGetters }, payload) {
    const { orderId, buyer_id: bId, billing_address_id: baId, shipping_address_id: saId } = payload
    const buyers = rootGetters[['company/companyBuyers']]
    const addresses = rootGetters['company/companyAddresses']
    const billingAddress = addresses.find(el => el.address_id === baId)
    const shippingAddress = addresses.find(el => el.address_id === saId)
    let updatedData = {}
    if (bId) {
      updatedData.buyer = buyers.find(el => el.buyer_id === bId)
      updatedData.buyer_id = bId
    }
    if (baId && billingAddress) {
      updatedData = {
        ...updatedData,
        billing_address: billingAddress,
        billing_address_id: baId
      }
    }
    if (saId && shippingAddress) {
      updatedData = {
        ...updatedData,
        shipping_address: shippingAddress,
        shipping_address_id: saId
      }
    }

    updatedData = {
      ...payload,
      ...updatedData
    }
    commit(type.UPDATE_ORDER, { orderId, updatedData })
  },
  async fetchOrders ({ state, commit, dispatch }, { page = 1, url = '/api/order?page=1' } = {}) {
    const mySeq = ++fetchOrdersSeq
    let allOrders = []
    let totalOrders = 0
    let totalPages = 1
    const pageSize = 20
    let newUrl = url.replace(/page=\d+/, `page=${page}`)
    const activeDraftOrderId = localStorage.getItem('activeDraftOrderId')
    if (activeDraftOrderId) {
      newUrl += `&activeDraftOrderId=${activeDraftOrderId}`
    }
    if (page <= state.totalOrderPages) {
      const response = await ordersService.fetchOrders(newUrl)
      if (mySeq !== fetchOrdersSeq) {
        return
      }
      const { data } = response.data
      allOrders = data.data
      totalOrders = data.total
      totalPages = Math.ceil(totalOrders / pageSize)
    }
    commit('SET_CURRENT_PAGE', page)
    commit('SET_TOTAL_ORDER_PAGES', totalPages)
    commit('SET_TOTAL_ORDERS', totalOrders)
    commit('SET_ORDERS_ORDERS', {
      orders: allOrders
    })

    const activeOrder = activeDraftOrderId
      ? allOrders.find((o) => o.id === parseInt(activeDraftOrderId))
      : null
    const cartProductIds = activeOrder?.order_has_products?.map((p) => p.product_id) ?? []
    dispatch(
      'catalog_products/syncActiveOrderFromCart',
      cartProductIds,
      { root: true }
    )
  },
  /**
   * Lightweight, draft-only feed for the header Orders dropdown. Constant-cost regardless
   * of how many orders the user has: no products/images/logos/addresses in the payload,
   * and it never touches `state.orders` / `activeDraftOrder` (the cart graph). page > 1
   * appends for infinite scroll; a ++seq stale-guard drops out-of-order responses.
   */
  async fetchDraftOrdersList ({ state, commit }, { page = 1 } = {}) {
    const mySeq = ++draftListSeq
    const response = await ordersService.fetchDraftOrdersList(`/api/order/draft-dropdown?page=${page}`)
    if (mySeq !== draftListSeq) {
      return
    }
    const { data } = response.data
    const rows = data.data
    commit('SET_DRAFT_ORDERS_LIST', page > 1 ? [...state.draftOrdersList, ...rows] : rows)
    commit('SET_DRAFT_LIST_PAGE', data.current_page)
    commit('SET_DRAFT_LIST_TOTAL_PAGES', data.total_pages)
  },
  /**
   * Refresh ONLY the active draft order. After a barcode-scan burst that is the one
   * order that changed, but fetchOrders() pulls 20 orders with every line item to get
   * there. Same end state for a fraction of the payload.
   *
   * UPDATE_ORDER merges into the copy already in state.orders and re-points
   * activeDraftOrder; if the order isn't in that list (user is on another page) it
   * would blank activeDraftOrder, so fall back to the full list in that case.
   */
  async refreshActiveDraftOrder ({ state, commit, dispatch }) {
    const activeDraftOrderId = localStorage.getItem('activeDraftOrderId')
    if (!activeDraftOrderId) {
      return
    }

    const orderId = parseInt(activeDraftOrderId)
    if (!state.orders?.some(o => o.id === orderId)) {
      await dispatch('fetchOrders')
      return
    }

    const { data } = await ordersService.getOrderDetail(orderId)
    if (!data?.id) {
      await dispatch('fetchOrders')
      return
    }

    commit(type.UPDATE_ORDER, { orderId, updatedData: data })

    // Keep the catalog's in-cart flags in step, same as fetchOrders does.
    dispatch(
      'catalog_products/syncActiveOrderFromCart',
      data.order_has_products?.map(p => p.product_id) ?? [],
      { root: true }
    )
  },
  async fetchPageOrders ({ state, commit }, { page = 1, url = '/api/order?page=1' } = {}) {
    let allOrders = []
    let totalOrders = 0
    let totalPages = 1
    const pageSize = 20
    let newUrl = url.replace(/page=\d+/, `page=${page}`)
    const activeDraftOrderId = localStorage.getItem('activeDraftOrderId')
    if (activeDraftOrderId) {
      newUrl += `&activeDraftOrderId=${activeDraftOrderId}`
    }
    if (page <= state.totalOrderPagePages) {
      const response = await ordersService.fetchOrders(newUrl)
      const { data } = response.data
      allOrders = data.data
      totalOrders = data.total
      totalPages = Math.ceil(totalOrders / pageSize)
      commit('SET_CURRENT_PAGE_ORDER', page)
      commit('SET_TOTAL_PAGE_ORDER_PAGES', totalPages)
      commit('SET_TOTAL_PAGE_ORDERS', totalOrders)
    } else if (state.totalOrderPagePages === 0) {
        const response = await ordersService.fetchOrders(newUrl)
        const { data } = response.data
        allOrders = data.data
    }

    commit('SET_ORDERS_PAGE_ORDERS', {
      orders: allOrders
    })

    commit('SET_ORDERS_ORDERS', {
      orders: allOrders
    })

  },
  async fetchCompletedOrders ({ state, commit }, { page = 1, url = '/api/order?page=1' } = {}) {
    let allOrders = []
    let totalOrders = 0
    let totalPages = 1
    const pageSize = 20
    let newUrl = url.replace(/page=\d+/, `page=${page}`)
    const activeDraftOrderId = localStorage.getItem('activeDraftOrderId')
    if (activeDraftOrderId) {
      newUrl += `&activeDraftOrderId=${activeDraftOrderId}`
    }
    if (page <= state.totalCompletedOrderPages) {
      const response = await ordersService.fetchOrders(newUrl)
      const { data } = response.data
      allOrders = data.data
      totalOrders = data.total
      totalPages = Math.ceil(totalOrders / pageSize)
      commit('SET_CURRENT_COMPLETED_ORDER_PAGE', page)
      commit('SET_TOTAL_COMPLETED_ORDER_PAGES', totalPages)
      commit('SET_TOTAL_COMPLETED_ORDERS', totalOrders)
    } else if (state.totalCompletedOrderPages === 0) {
        const response = await ordersService.fetchOrders(newUrl)
        const { data } = response.data
        allOrders = data.data
    }
    commit('SET_COMPLETED_ORDERS', {
      orders: allOrders
    })
  },
  async fetchExceedQuantitiesUnit ({ commit }, orderId) {
    const { data: { results } } = await ordersService.fetchExceedQuantitiesUnit(orderId)
    commit(type.SET_EXCEED_QUANTITY_PRODUCT, results)
    return results
  },
  async addProduct ({ commit }, { productOrder, productId }) {
    try {
      await axios.post('/api/order/add-products', productOrder)
    } catch (error) {
      if (handleMoqError(error)) return
      throw error
    }

    commit(type.ADD_ORDER_PRODUCT, { order_id: productOrder.order_id, productId })
  },
  async removeProduct ({ commit }, { orderId, productId }) {
    await ordersService.removeProduct({
      order_id: orderId,
      product_id: productId
    })

    commit(type.REMOVE_ORDER_PRODUCT, { orderId, productId })
  },
  async addProducts ({ commit }, { orderId, productIds, sizeId, quantity }) {
    await axios.post('/api/order/add-products', {
      order_id: orderId,
      product_ids: productIds,
      sizeId: sizeId,
      quantity: quantity
    })
    commit(type.ADD_ORDER_PRODUCTS, { orderId, productIds })
  },
  async fetchOrderDetail ({ commit }, { orderId }) {
    const { data: { data } } = await axios.get(`/api/order/${orderId}`)

    commit(type.SET_ORDERS_ORDERS, {
      orders: data.data
    })
  },
  async updateOrder ({ commit }, { orderId, data, updatedOrder }) {
    try {
      await ordersService.updateOrders(orderId, data)
      commit(type.UPDATE_ORDER, { orderId, updatedData: updatedOrder })
    } catch (error) {
      throw error
    }
  },
  async deleteOrderById ({ commit }, { orderId, isActiveOrder = false }) {
    try {
      const { data: { message } } = await ordersService.deleteOrderById(orderId)
      if (isActiveOrder) {
        commit(type.CLEAR_ACTIVE_DRAFT_ORDER)
        commit('catalog_products/CLEAR_ACTIVE_COLLECTION', null, { root: true })
      }
      commit(type.REMOVE_ORDER, { orderId })
      return message
    } catch (error) {
      console.log('Error: ', error)
    }
  },
  async bulkDeleteDraftOrder ( {commit}, { orderIds }) {
    const { data: { message } } = await ordersService.bulkDeleteDraftOrder({
      data: {
        order_ids: orderIds
      }
    })

    const activeDraftOrderId = localStorage.getItem('activeDraftOrderId');
    if (activeDraftOrderId && orderIds.includes(parseInt(activeDraftOrderId))) {
       commit(type.CLEAR_ACTIVE_DRAFT_ORDER);
    }

    commit(type.BULK_DELETE_DRAFT_ORDER, message)
    return message
  },
  async copyOrderToNewOrder ({ commit }, { ...payload }) {
    try {
      const { data } = await ordersService.copyActiveOrderToNewOrder(payload)
      commit(type.ADD_ORDER, { data })
    } catch (error) {
      console.log('Error: ', error)
    }
  },
  async copyDraftOrderToNewDraftOrder ({ commit }, payload) {
    try {
      const { data: { message } } = await ordersService.copyDraftOrderToOtherDraftOrder(payload)
      return message
    } catch (error) {
      console.log('Error: ', error)
    }
  },
  async getUnitSummaryReportData ({ commit }, orderId) {
    // Without an order there is no report to fetch, and requesting one anyway
    // put `null` in the url, which the api answered with a 404 the axios
    // interceptor toasted as "No query results for model [App\Models\Order]".
    if (!orderId || orderId === 'null') {
      commit(type.FETCH_UNIT_SUMMARY_REPORT_DATA, { data: null })
      return
    }

    try {
      const { data } = await ordersService.getUnitSummaryReportData(orderId)
      commit(type.FETCH_UNIT_SUMMARY_REPORT_DATA, {
        data: data
      })
      return
    } catch (error) {
      console.log('Error: ', error)
    }
  },
  patchUnitSummaryTotals ({ commit }, totals) {
    commit(type.PATCH_UNIT_SUMMARY_TOTALS, totals)
  },
  // Add this action to the existing actions
  async getDigitalCatalogUnitSummaryReportData ({ commit }) {
    try {
        const { data } = await ordersService.getDigitalCatalogUnitSummaryReportData()
        commit(type.FETCH_UNIT_SUMMARY_REPORT_DATA, {
            data: data
        })
        return data
    } catch (error) {
        console.log('Error: ', error)
        throw error
    }
  },
  clearActiveDraftOrder ({ commit }) {
    commit(type.CLEAR_ACTIVE_DRAFT_ORDER)
  },
  async newOrder ({ commit }, payload) {
    try {
      return await ordersService.newOrder(payload)
    } catch (error) {
      console.log('Error: ', error)
    }
  },
  async getNewOrderModalData ({ commit }, payload) {
    try {
      return await ordersService.getNewOrderModalData(payload)
    } catch (error) {
      console.log('Error: ', error)
    }
  },
  setNewOrderMode ({ commit }, payload) {
    commit(type.SET_NEW_ORDER_MODE, payload)
  },
  setNewOrderProduct ({ commit }, payload) {
    commit(type.SET_NEW_ORDER_PRODUCT, payload)
  },
  clearNewOrderProduct ({ commit }) {
    commit(type.CLEAR_NEW_ORDER_PRODUCT)
  },
  async getOrderDetail ({ commit }, payload) {
    try {
      const { data } = await ordersService.getOrderDetail(payload)
      commit(type.GET_ORDER_DETAIL, data)
    } catch (error) {
      console.log(error)
    }
  },
  async completeOrder ({ commit }, { data }) {
    try {
       const response = await ordersService.completeOrder(data)
       return response.data
    } catch (error) {
      console.log('Error: ', error)
      // Rethrow: swallowing this returned undefined, and checkout only noticed because
      // reading order_id off it happened to throw. Optional-chaining that read would have
      // opened the success modal on a 500 that completed nothing.
      throw error
    }
  },
  async deleteOrderedProductNote ({ commit }, productId) {
    try {
      await ordersService.deleteProductNote({
        data: {
          product_id: productId
        }
      })
    } catch (error) {
      console.log('Error: ', error)
    }
  },
  async validateOrderedProduct({commit} , orderId) {
      try {
            const response = await ordersService.validateOrderedProduct(orderId)
            commit(type.SET_VALIDATION_RESPONSE, response.data)
      } catch (error) {
          console.log('Error: ', error)
      }
  },
  async addQuantityExceedProducts ({ commit }, payload) {
    const { data: { results } } = await ordersService.addQuantityExceedProducts(payload)
  },
  async updatePo({ commit }, { orderId, data }) {
    try {
        const response = await ordersService.updatePo(orderId, data);
        return response.data;
    } catch (error) {
        console.log('Error updating PO:', error);
        throw error;
    }
  },
  setActiveDraftOrder ({ commit }, orderId) {
    commit(type.SET_ACTIVE_DRAFT_ORDER, orderId)
  },
  isOrderHasProductQuantities ({ commit }, isOrderHasProductQuantities) {
    commit(type.ORDER_HAS_PRODUCT_QUANTITIES, isOrderHasProductQuantities)
  },
  inSufficientQuantitySelectedProduct ({ commit }, product) {
    commit(type.INSUFFICIENT_QUANTITY_SELECTED_PRODUCT, product)
  },
  setActionRequiredSelectedOption ({ commit }, selectedOption) {
    commit(type.ACTION_REQUIRED_SELECTED_OPTION, selectedOption)
  },
  async addProductByDesignNumber (_, payload) {
    try {
      const response = await ordersService.addProductByDesignNumber(payload)
      if (response.data.success) {
        return { success: true, data: response.data }
      }
      return { success: false, error: response.data.message }
    } catch (error) {
      const status = error.response?.status
      // Only a 404 actually means the design number is unknown. Anything else carrying no
      // message (a throttle, a 500) would otherwise tell the operator to go re-check a
      // barcode that is perfectly fine.
      return {
        success: false,
        error: status === 429
          ? 'Scanning too fast — wait a moment and scan again'
          : (error.response?.data?.message || 'Product Not Exists For This Design Number'),
        status
      }
    }
  },
  async refreshOrderInventory (_, orderId) {
    try {
      const response = await ordersService.refreshOrderInventory(orderId)
      return { success: true, data: response.data }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message,
        status: error.response?.status
      }
    }
  },
  async downloadJpgImages (_, orderId) {
    try {
      const response = await ordersService.downloadJpgImages(orderId)
      const disposition = response.headers['content-disposition'] || ''
      const match = disposition.match(/filename="?([^";]+)"?/i)
      const filename = match ? match[1] : 'order-images'
      const url = window.URL.createObjectURL(response.data)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      let message = 'Failed to download images.'
      if (err.response && err.response.data instanceof Blob) {
        try {
          const text = await err.response.data.text()
          const parsed = JSON.parse(text)
          message = parsed.message || parsed?.error?.message || message
        } catch (_) {}
      }
      Vue.$toast.error(message)
    }
  },
  async duplicateOrder ({ commit }, sourceOrderId) {
    const { data } = await ordersService.duplicateOrder(sourceOrderId)
    const newOrder = data.data.order
    commit(type.ADD_ORDER, newOrder)
    commit(type.SET_ACTIVE_DRAFT_ORDER, newOrder.id)
    if (data?.data?.validation && data.data.validation.status === false) {
      commit(type.SET_VALIDATION_RESPONSE, data.data.validation)
    }
    return data.data
  },
  async editRecreateOrder ({ commit }, sourceOrderId) {
    const { data } = await ordersService.editRecreateOrder(sourceOrderId)
    const newOrder = data.data.order
    commit(type.ADD_ORDER, newOrder)
    commit(type.SET_ACTIVE_DRAFT_ORDER, newOrder.id)
    if (data?.data?.validation && data.data.validation.status === false) {
      commit(type.SET_VALIDATION_RESPONSE, data.data.validation)
    }
    return data.data
  }
}
